/**
 * Read a display title out of the i18n JSONB the itinerary tables store.
 *
 * The column carries THREE shapes, all of them live in production data:
 *
 *   - the i18n array `[{ text, languageCode, default }]` — the overwhelming
 *     majority of rows;
 *   - a language-keyed object `{ en: '…' }`;
 *   - a bare string.
 *
 * Every surface that shows one of these titles has to handle all three, and
 * the failure mode when it does not is the worst kind: a partial read falls
 * through to stringifying the value, so the screen shows a wall of raw JSON
 * where a name belongs. That happened on the status action log, which is why
 * this lives in `shared` rather than being reimplemented per domain — one
 * definition both the API and any web page can reach.
 *
 * Returns an empty string when there is no usable text, NEVER the raw value.
 * A caller that wants a placeholder supplies its own.
 */
export const resolveI18nTitle = (
  title: unknown,
  languageCode?: string | null
): string => {
  if (typeof title === 'string') return title;

  if (Array.isArray(title)) {
    const entries = title as Array<{
      text?: string | null;
      languageCode?: string | null;
      default?: boolean | null;
    }>;
    // The requested language, then the entry marked default, then English,
    // then whatever has text — so a title always resolves if any exists.
    const lang = languageCode?.toLowerCase();
    const byLang = lang
      ? entries.find((e) => e.languageCode?.toLowerCase() === lang && e.text)
      : undefined;
    const byDefault = entries.find((e) => e.default && e.text);
    const byEn = entries.find((e) => e.languageCode === 'en' && e.text);
    const first = entries.find((e) => e.text);
    return (byLang ?? byDefault ?? byEn ?? first)?.text ?? '';
  }

  if (title && typeof title === 'object' && 'en' in title) {
    return String((title as Record<string, unknown>).en ?? '');
  }

  return '';
};

/**
 * The same read, but tolerant of a value that arrives as a JSON STRING.
 *
 * A server that selected the column without extracting the text sends the
 * whole structure through as text. Parsing it here means a stale or
 * half-fixed API degrades to a readable name rather than to raw JSON on
 * screen — the guarantee the log page needs, since it is the screen where
 * this went wrong.
 */
export const resolveI18nTitleLoose = (
  title: unknown,
  languageCode?: string | null
): string => {
  if (typeof title === 'string') {
    const trimmed = title.trim();
    // Only attempt a parse when it actually looks like encoded JSON; a plain
    // title beginning with a bracket is not worth mangling over.
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        const resolved = resolveI18nTitle(parsed, languageCode);
        if (resolved) return resolved;
        // Parsed but yielded nothing usable: returning the JSON would be the
        // very output this exists to prevent.
        return '';
      } catch {
        // Not JSON after all — it is just a title that starts with a bracket.
        return title;
      }
    }
    return title;
  }
  return resolveI18nTitle(title, languageCode);
};
